import { Box } from '@chakra-ui/react';
import { appUpdateNavigationGuard } from '@ionq/app-update';
import { InsetShell, Sidebar, SkipToContent, Toaster } from '@ionq/legacy-ui';
import { type LoaderFunctionArgs, Outlet, useLoaderData } from 'react-router';
import { identifyLdUser } from '../lib/launchDarkly';
import { fetchSessionUser } from '../lib/sessionUser';
import { AppNavbar } from './AppNavbar';
import AppSidebar from './AppSidebar';
import { SessionExpiredDialog } from './SessionExpiredDialog';
import { SessionProvider } from './SessionProvider';

const SIDEBAR_WIDTH = '256px';

// Root loader: app-update guard, then the session. The remote's requireAuth
// middleware usually wins the signed-out race (middleware runs before
// loaders); the UNAUTHORIZED branch is defense-in-depth.
export async function sessionLoader({ request }: LoaderFunctionArgs) {
    appUpdateNavigationGuard(request);
    const user = await fetchSessionUser(request);
    // Flags resolved for the session user BEFORE first render.
    await identifyLdUser(user);
    return user;
}

// Shell layout: same header and sidebar chrome as enterprise, on the real
// session.
export default function AppLayout() {
    const user = useLoaderData<typeof sessionLoader>();

    return (
        <SessionProvider user={user}>
            <Sidebar.Provider>
                <Toaster />
                <SkipToContent />
                <SessionExpiredDialog />
                {/* The shared flat shell (legacy-ui, sidebar-01 geometry):
                    bordered rail + top bar + the page panel, which is the
                    one scroll container. The federated remote is
                    light-audited only: its pages render their light
                    surfaces even in dark mode (accepted — the chrome and
                    shell-owned pages follow the user's choice). The remote
                    paints its surfaces transparent, so the panel paints bg
                    itself. */}
                <InsetShell>
                    <AppSidebar width={SIDEBAR_WIDTH} zIndex='1000' />
                    <InsetShell.Column>
                        <AppNavbar />
                        <InsetShell.Panel>
                            <Box
                                as='main'
                                id='main-content'
                                // Skip-link target: tabIndex so the jump
                                // moves real focus.
                                tabIndex={-1}
                                outline='none'
                                position='relative'
                                // The remote's standalone layout ends every
                                // page with 64px of air (its AppLayout's
                                // mb); the federated tree mounts here
                                // without that wrapper, so the shell adds it.
                                pb='64px'
                            >
                                <Outlet />
                            </Box>
                        </InsetShell.Panel>
                    </InsetShell.Column>
                </InsetShell>
                {/* The shell PANEL scrolls (not the document): restore its
                    offset on Back, reset on pushes. AFTER the shell — see
                    shell-scroll.tsx for why the order matters. */}
                <InsetShell.ScrollRestoration />
            </Sidebar.Provider>
        </SessionProvider>
    );
}
